import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, TextField, Switch, FormControlLabel, Autocomplete, MenuItem, Chip, CircularProgress } from '@mui/material';
import SuperAdminService, { BranchOption, FiscalDeviceCreateDto } from '../../services/SuperAdminService';
import { AppDatePicker } from '../common/AppDatePicker';

/** Hat operatörleri — sabit liste; listede olmayanlar için "Diğer". */
export const LINE_OPERATORS = ['Turkcell', 'Vodafone', 'Türk Telekom', 'Diğer'];

export const emptyFiscalDeviceForm: FiscalDeviceCreateDto = {
  companyId: '',
  vkn: '',
  authorizedPerson: '',
  phone: '',
  email: '',
  lineNumber: '',
  lineOwner: '',
  lineOperator: '',
  accountName: '',
  city: '',
  branch: '',
  branchId: '',
  brand: '',
  model: '',
  serialNumber: '',
  usageArea: '',
  gmuFeeCollected: false,
  activationDate: null,
  expiryDate: null,
  invoiceStatus: '',
  notes: '',
};

/** Cihaz kaydını forma çevirir (düzenleme açılışı). */
export function fiscalDeviceToForm(device: {
  companyId: string;
  vkn: string | null;
  authorizedPerson: string | null;
  phone: string | null;
  email?: string | null;
  lineNumber: string | null;
  lineOwner: string | null;
  lineOperator: string | null;
  accountName: string | null;
  city: string | null;
  branch: string | null;
  branchId?: string | null;
  brand: string | null;
  model: string | null;
  serialNumber: string | null;
  usageArea: string | null;
  gmuFeeCollected: boolean;
  activationDate: string | null;
  expiryDate: string | null;
  invoiceStatus: string | null;
  notes: string | null;
}): FiscalDeviceCreateDto {
  return {
    companyId: device.companyId,
    vkn: device.vkn || '',
    authorizedPerson: device.authorizedPerson || '',
    phone: device.phone || '',
    email: device.email || '',
    lineNumber: device.lineNumber || '',
    lineOwner: device.lineOwner || '',
    lineOperator: device.lineOperator || '',
    accountName: device.accountName || '',
    city: device.city || '',
    branch: device.branch || '',
    branchId: device.branchId || '',
    brand: device.brand || '',
    model: device.model || '',
    serialNumber: device.serialNumber || '',
    usageArea: device.usageArea || '',
    gmuFeeCollected: device.gmuFeeCollected,
    activationDate: device.activationDate ? device.activationDate.split('T')[0] : null,
    expiryDate: device.expiryDate ? device.expiryDate.split('T')[0] : null,
    invoiceStatus: device.invoiceStatus || '',
    notes: device.notes || '',
  };
}

interface FiscalDeviceFormFieldsProps {
  form: FiscalDeviceCreateDto;
  onChange: (field: keyof FiscalDeviceCreateDto, value: any) => void;
  /** Daha önce kaydedilmiş hat sahipleri — serbest yazıma ek öneri listesi. */
  lineOwnerOptions: string[];
}

/**
 * Yazarkasa cihazı form alanları.
 *
 * Hem şirket kartından açılan `FiscalDeviceModal` hem de süperadmin
 * "Yazarkasalar" sekmesindeki `FiscalDeviceFormDialog` bu bileşeni kullanır;
 * yeni bir alan eklendiğinde tek yerde güncellenmesi için ayrıldı.
 */
const FiscalDeviceFormFields: React.FC<FiscalDeviceFormFieldsProps> = ({ form, onChange, lineOwnerOptions }) => {
  // Eski bir kayıtta listede olmayan bir operatör varsa düzenlemede kaybolmasın.
  const operatorOptions = form.lineOperator && !LINE_OPERATORS.includes(form.lineOperator)
    ? [...LINE_OPERATORS, form.lineOperator]
    : LINE_OPERATORS;

  const [branchOptions, setBranchOptions] = useState<BranchOption[]>([]);
  const [branchesLoading, setBranchesLoading] = useState(false);

  const companyId = form.companyId;

  useEffect(() => {
    if (!companyId) {
      setBranchOptions([]);
      return;
    }
    let cancelled = false;
    setBranchesLoading(true);
    SuperAdminService.getInstance()
      .getCompanyBranchOptions(companyId)
      .then(opts => { if (!cancelled) setBranchOptions(opts); })
      .catch(() => { if (!cancelled) setBranchOptions([]); })
      .finally(() => { if (!cancelled) setBranchesLoading(false); });
    return () => { cancelled = true; };
  }, [companyId]);

  // Şirket değiştirilirse önceki şirketin şubesi seçili kalmasın. İlk render'da
  // (düzenlemede kayıtlı şube yüklenirken) temizleme yapılmaz.
  const previousCompanyIdRef = useRef<string | null>(null);
  useEffect(() => {
    const previous = previousCompanyIdRef.current;
    previousCompanyIdRef.current = companyId;
    if (previous === null || previous === companyId) return;
    if (form.branch || form.branchId) {
      onChange('branch', '');
      onChange('branchId', '');
    }
    // form/onChange bağımlılığa eklenmez: etki yalnızca şirket değişiminde çalışmalı.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Şube listeden seçilmeden önce kaydedilmiş cihazlarda yalnızca şube adı var;
  // Autocomplete'te boş görünmesin diye ad, geçici bir seçenek olarak eklenir.
  const branchChoices = useMemo(() => {
    const legacyName = (form.branch || '').trim();
    if (!legacyName || branchOptions.some(b => b.name === legacyName)) return branchOptions;
    return [...branchOptions, { branchId: '', name: legacyName, isActive: true }];
  }, [branchOptions, form.branch]);

  const selectedBranch = (form.branchId
    ? branchChoices.find(b => b.branchId === form.branchId)
    : branchChoices.find(b => b.name === (form.branch || '').trim())) || null;

  /**
   * Şube seçilince şubenin (franchise ise kendi firmasının) bilgileri forma
   * taşınır. Yalnızca boş alanlar doldurulur; elle girilmiş bir değer ezilmez.
   */
  const handleBranchSelect = (branch: BranchOption | null) => {
    onChange('branchId', branch?.branchId || '');
    onChange('branch', branch?.name || '');
    if (!branch) return;

    const fillIfEmpty = (field: keyof FiscalDeviceCreateDto, value?: string | null) => {
      if (value && !String(form[field] ?? '').trim()) onChange(field, value);
    };

    fillIfEmpty('city', branch.city);
    fillIfEmpty('accountName', branch.firmName);
    fillIfEmpty('vkn', branch.firmTaxNumber);
    fillIfEmpty('phone', branch.firmPhone || branch.phone);
  };

  const hasBranches = branchOptions.length > 0;

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
      <TextField size="small" label="VKN" value={form.vkn || ''} onChange={e => onChange('vkn', e.target.value)} />
      <TextField size="small" label="Yetkili" value={form.authorizedPerson || ''} onChange={e => onChange('authorizedPerson', e.target.value)} />
      <TextField size="small" label="Telefon" value={form.phone || ''} onChange={e => onChange('phone', e.target.value)} />
      <TextField
        size="small"
        type="email"
        label="E-posta"
        placeholder="Süre bitimi bildirimi için"
        value={form.email || ''}
        onChange={e => onChange('email', e.target.value)}
      />
      <TextField
        size="small"
        label="Hat No"
        placeholder="Mobil hat verildiyse"
        value={form.lineNumber || ''}
        onChange={e => onChange('lineNumber', e.target.value)}
      />
      <Autocomplete
        freeSolo
        size="small"
        options={lineOwnerOptions}
        value={form.lineOwner || ''}
        onChange={(_, v) => onChange('lineOwner', v || '')}
        onInputChange={(_, v) => onChange('lineOwner', v)}
        renderInput={params => (
          <TextField {...params} label="Hat Sahibi" placeholder="Aquasoft ya da firma adı" />
        )}
      />
      <TextField
        select
        size="small"
        label="Operatör"
        value={form.lineOperator || ''}
        onChange={e => onChange('lineOperator', e.target.value)}
      >
        <MenuItem value=""><em>Seçilmedi</em></MenuItem>
        {operatorOptions.map(op => (
          <MenuItem key={op} value={op}>{op}</MenuItem>
        ))}
      </TextField>
      <TextField size="small" label="Cari Unvan" value={form.accountName || ''} onChange={e => onChange('accountName', e.target.value)} />
      <TextField size="small" label="İl" value={form.city || ''} onChange={e => onChange('city', e.target.value)} />
      {/* Şirketin şubesi varsa listeden seçilir (kimliğiyle birlikte kaydedilir);
          şubesiz şirketlerde alan eskisi gibi serbest metin kalır. */}
      {hasBranches || branchesLoading ? (
        <Autocomplete
          size="small"
          options={branchChoices}
          loading={branchesLoading}
          disabled={!companyId}
          getOptionLabel={opt => opt.name}
          isOptionEqualToValue={(opt, val) => opt.branchId === val.branchId && opt.name === val.name}
          value={selectedBranch}
          onChange={(_, v) => handleBranchSelect(v)}
          renderOption={(props, option) => (
            <li {...props} key={option.branchId || option.name}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <span>{option.name}</span>
                {option.isFranchise && <Chip label="Franchise" size="small" color="info" sx={{ height: 18, fontSize: '0.65rem' }} />}
                {!option.isActive && <Chip label="Pasif" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />}
              </Box>
            </li>
          )}
          renderInput={params => (
            <TextField
              {...params}
              label="Şube"
              placeholder={companyId ? 'Şube ara/seç' : 'Önce şirket seçin'}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {branchesLoading ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      ) : (
        <TextField size="small" label="Şube" value={form.branch || ''} onChange={e => onChange('branch', e.target.value)} />
      )}
      <TextField size="small" label="Marka" value={form.brand || ''} onChange={e => onChange('brand', e.target.value)} />
      <TextField size="small" label="Model" value={form.model || ''} onChange={e => onChange('model', e.target.value)} />
      <TextField size="small" label="Seri No" value={form.serialNumber || ''} onChange={e => onChange('serialNumber', e.target.value)} />
      <TextField size="small" label="Kullanım Yeri" value={form.usageArea || ''} onChange={e => onChange('usageArea', e.target.value)} />
      <AppDatePicker label="Aktivasyon Tarihi" value={form.activationDate || ''} onChange={v => onChange('activationDate', v || null)} />
      <AppDatePicker label="Bitiş Tarihi" value={form.expiryDate || ''} onChange={v => onChange('expiryDate', v || null)} />
      <TextField size="small" label="Fatura Durumu" value={form.invoiceStatus || ''} onChange={e => onChange('invoiceStatus', e.target.value)} />
      <FormControlLabel
        control={<Switch checked={form.gmuFeeCollected} onChange={e => onChange('gmuFeeCollected', e.target.checked)} />}
        label="GMÜ Ücreti Alındı"
        sx={{ ml: 0 }}
      />
      <TextField size="small" label="Notlar" value={form.notes || ''} onChange={e => onChange('notes', e.target.value)} multiline sx={{ gridColumn: { md: 'span 2' } }} />
    </Box>
  );
};

export default FiscalDeviceFormFields;
